import {
  ListItem,
  QnAAnswersWithComments,
  QuestionAnswerCard,
} from '@ninjacart/nc-nk-adaptive-cards';
import {
  QnAPlaceHolder,
  useQnaFeedStore,
  createCommentQnA,
  getQnACommentList,
  onQnAReplyLike,
} from '@ninjacart/nc-nk-advisory';
import { UserProps, useProfileStore } from '@ninjacart/nc-nk-store';
import { Paragraph, Spinner, View } from '@ninjacart/nc-nk-ui';
import { useIsFocused, useScreenDimensions } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUserEngagementStore from '../store/useUserEngagementStore';
import {ProfileTabEmptyState} from './profileTabEmptyState';
import StreamComments from './streamComments';

type ActivityParams = {
  profileData: UserProps;
  id?: string;
  hasUserLoggedIn: boolean | null;
  onReportBottomSheetOpen: Function;
};

const ProfileAllActivityComments = ({
  profileData,
  id,
  hasUserLoggedIn,
  onReportBottomSheetOpen,
}: ActivityParams) => {
  const [refreshingComments, setRefreshingComments] = useState(false);
  const isProfileActivityLoaded = useUserEngagementStore((state) => state.isProfileActivityLoaded);
  const profileActivity = useUserEngagementStore((state) => state.profileActivity);
  const fetchProfileActivity = useUserEngagementStore((state) => state.fetchProfileActivity);
  const resetProfileActivity = useUserEngagementStore((state) => state.resetProfileActivity);

  const startCountCommentsRef = useRef({ start: 0, count: 10 });
  const [onEndReachedSpinnerComments, setOnEndReachedSpinnerComments] = useState(false);
  const screenDimensions = useScreenDimensions();
  const userProfile = useProfileStore((state) => state.userProfile);
  const isFocused = useIsFocused();
  const { t } = useTranslation();
  const userId = id ?? userProfile?.user?.id!;

  const activityListPayloadRef = useRef({
    userId: userId,
    offset: startCountCommentsRef.current.start,
    count: startCountCommentsRef.current.count,
  });

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    if (isFocused) {
      activityListPayloadRef.current.offset = 0;
      resetProfileActivity();
      fetchProfileActivity(activityListPayloadRef?.current, false);
    }
  }, [isFocused]);

  const onPullToRefreshComments = async () => {
    setRefreshingComments(true);
    resetProfileActivity();
    startCountCommentsRef.current.start = 0;
    activityListPayloadRef.current.offset = 0;
    try {
      await fetchProfileActivity(activityListPayloadRef?.current, true);
    } catch (err) {
      console.log('err activity list onPullToRefreshComments', err);
    } finally {
      setRefreshingComments(false);
    }
  };

  const sortProfileActivity = (activity) => {
    return activity.sort((a, b) => {
      const dateA =
        a.subType === 'like'
          ? a.actionData?.content?.likeCreatedAt
          : a.actionData?.content?.commentCreatedAt;
      const dateB =
        b.subType === 'like'
          ? b.actionData?.content?.likeCreatedAt
          : b.actionData?.content?.commentCreatedAt;
      const timeA = dateA ? new Date(dateA).getTime() : 0;
      const timeB = dateB ? new Date(dateB).getTime() : 0;

      return timeB - timeA;
    });
  };
  const sortedProfileActivity = useMemo(() => {
    const allActivity = [
      ...(profileActivity?.comments || []).map((item) => ({ ...item, subType: 'comment' })),
      ...(profileActivity?.likes || []).map((item) => ({ ...item, subType: 'like' })),
    ];
    return sortProfileActivity(allActivity);
  }, [profileActivity]);

  const UserActivityMessage = ({ message, children }) => {
    return (
      <View
        style={{
          paddingHorizontal: 8,
          marginBottom: 8,
          backgroundColor: '$grey',
        }}
      >
        <View style={{ flexDirection: 'row', paddingTop: 2, alignItems: 'center' }}>
          <Paragraph
            fontSize={14}
            marginLeft={4}
            paddingVertical={8}
            style={{ fontWeight: 'bold' }}
          >
            {profileData?.user?.id === userProfile?.user?.id
              ? t('page.you')
              : profileData?.user?.name}
          </Paragraph>
          <Paragraph fontSize={14} marginLeft={4}>
            {message}
          </Paragraph>
        </View>
        {children}
      </View>
    );
  };

  const renderCommentsList = ({ item, index }) => {
    console.log('item here ', item);
    if (item.subType == 'comment') {
      return (
        <UserActivityMessage message={t('page.commentedOnThis')}>
          {item.type === 'STREAM' ? (
            <>
              <ListItem
                item={item}
                index={index}
                userProfile={userProfile}
                screenDimensions={screenDimensions}
                onReportBottomSheetOpen={() => {}}
                onCommentBottomSheetOpen={() => {}}
                pageName="Profile Detail"
                isAutoPlayBannerAdEnabled={false}
              />
              <StreamComments
                item={item?.actionData?.content}
                profileName={profileData?.user?.name}
                index={index}
              />
            </>
          ) : (
            <>
              <QuestionAnswerCard
                item={item?.actionData?.content}
                index={index}
                questionId=""
                pageName="profile detail"
                useQnaFeedStore={useQnaFeedStore}
                createCommentQnA={createCommentQnA}
                getQnACommentList={getQnACommentList}
                onQnAReplyLike={onQnAReplyLike}
              />
              {item?.actionData?.content?.children.map((el) => (
                <QnAAnswersWithComments
                  reply={el}
                  showFeedback={true}
                  createCommentQnA={createCommentQnA}
                  getQnACommentList={getQnACommentList}
                  onQnAReplyLike={onQnAReplyLike}
                  useQnaFeedStore={useQnaFeedStore}
                  pageExpertName="profile-detail"
                  pageName="profile-detail"
                  index={index}
                />
              ))}
            </>
          )}
        </UserActivityMessage>
      );
    } else {
      return (
        <UserActivityMessage message={t('page.likedThis')}>
          {item.type === 'STREAM' ? (
            <ListItem
              item={item}
              index={index}
              userProfile={userProfile}
              screenDimensions={screenDimensions}
              onReportBottomSheetOpen={onReportBottomSheetOpen}
              onCommentBottomSheetOpen={() => {}}
              pageName="Profile Detail"
              isAutoPlayBannerAdEnabled={false}
            />
          ) : (
            <QuestionAnswerCard
              item={item?.actionData?.content}
              index={index}
              questionId=""
              // expertAnswer={item?.actionData?.content?.children?.find(
              //   (x) => x.creatorRole === 'mods'
              // )}
              pageName="profile detail"
              useQnaFeedStore={useQnaFeedStore}
              createCommentQnA={createCommentQnA}
              getQnACommentList={getQnACommentList}
              onQnAReplyLike={onQnAReplyLike}
            />
          )}
        </UserActivityMessage>
      );
    }
  };

  const onEndReachedComments = async () => {
    setOnEndReachedSpinnerComments(true);
    try {
      startCountCommentsRef.current.start += startCountCommentsRef.current.count;
      activityListPayloadRef.current.offset = startCountCommentsRef.current.start;
      await fetchProfileActivity(activityListPayloadRef?.current, false);
    } catch (err) {
      console.log('err activity list onEndReachedComments', err);
    } finally {
      setOnEndReachedSpinnerComments(false);
    }
  };

  const ListEmptyComponent = () =>
    isProfileActivityLoaded && !sortedProfileActivity?.length && <ProfileTabEmptyState />;

  if (!isProfileActivityLoaded) return <QnAPlaceHolder showDetailPage={true} />;

  return (
    <View flex={1} backgroundColor={'$background'}>
      <FlashList
        data={sortedProfileActivity}
        renderItem={renderCommentsList}
        getItemType={(item) => item.type}
        keyExtractor={(item, index) => index.toString()}
        scrollEventThrottle={24}
        estimatedItemSize={430}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={0.8}
        onEndReached={onEndReachedComments}
        refreshing={refreshingComments}
        onRefresh={onPullToRefreshComments}
        ListFooterComponent={() =>
          onEndReachedSpinnerComments && (
            <View style={{ flex: 1, padding: 10, alignItems: 'center' }}>
              <Spinner size="large" color="$green10" />
            </View>
          )
        }
        ListFooterComponentStyle={{ height: 100 }}
        ListEmptyComponent={ListEmptyComponent}
      />
    </View>
  );
};

export default ProfileAllActivityComments;
